import { analytics } from "./port";
const SECTION = "SBC";
const SBC_CATEGORY_BY_ID = {
    1: "player",
    2: "upgrade",
    3: "challenge",
    4: "icon",
    5: "foundation",
    6: "swap",
};
export const getSbcCategory = (categoryId) => categoryId != null ? SBC_CATEGORY_BY_ID[categoryId] : undefined;
export const getSbcScope = (playersFrom) => playersFrom;
/** Human-readable filter keys, in the order web has always emitted them. */
export const buildSbcFilterKeys = (filters) => {
    if (!filters)
        return [];
    const keys = [];
    if (filters.useUntradeablesOnly)
        keys.push("Only Use Untradeables");
    if (filters.prioritizeSbcStorage)
        keys.push("Prioritise SBC Storage");
    if (filters.prioritizeDuplicates)
        keys.push("Prioritise Duplicates");
    if (filters.includeTransferlist)
        keys.push("Prioritise Transfer List");
    if (filters.excludeSbcPlayers)
        keys.push("Exclude SBC Players");
    if (filters.excludeObjectivePlayers)
        keys.push("Exclude Objectives");
    if (filters.excludeActiveSquad)
        keys.push("Exclude Active Squad");
    if (filters.minRating != null)
        keys.push("Min Rating");
    if (filters.maxRating != null)
        keys.push("Max Rating");
    if (filters.includedPlayers.length > 0)
        keys.push("Must Include Player");
    if (filters.excludedPlayers.length > 0)
        keys.push("Must Exclude Player");
    return keys;
};
/**
 * The canonical `solve_sbc` / `solve_sbc_again` property bag. Both apps build
 * from here so the two platforms stay one taxonomy; only the transport differs.
 */
export const buildSolveSbcProps = ({ subsection, categoryId, filters, scope, }) => ({
    section: SECTION,
    subsection,
    sbc_category: getSbcCategory(categoryId),
    scope: getSbcScope(scope ?? filters?.playersFrom),
    filters: buildSbcFilterKeys(filters),
    action_type: "core",
});
/** Fire from the app that owns the port (mobile). No-op on web (no port). */
export const trackSolveSbc = (args) => {
    analytics.track("solve_sbc", buildSolveSbcProps(args));
};
/** Solve re-run from the solution view. No-op on web (no port). */
export const trackSolveSbcAgain = (args) => {
    analytics.track("solve_sbc_again", buildSolveSbcProps(args));
};
/**
 * In-app feedback submitted (DEV-3328). Soft dependency on the analytics
 * foundation: a no-op until the app registers its port, so it never blocks a
 * submission. No-op on web (no port).
 */
export const trackFeedbackSent = (category) => {
    analytics.track("feedback_sent", { category });
};
/* -------------------------------------------------------------------------- */
/* Sentiment prompt                                                           */
/* -------------------------------------------------------------------------- */
/*
  The "how are you finding the app?" nudge. `shown` is fired separately from the
  answer because the gap between them is the only read we get on how many people
  the cap actually reaches, and a dismissal is a different signal from silence.
*/
export const trackSentimentPromptShown = () => {
    analytics.track("sentiment_prompt_shown");
};
export const trackSentimentPromptAnswered = (sentiment) => {
    analytics.track("sentiment_prompt_answered", { sentiment });
};
/** Closed without answering — the case that earns the single re-ask. */
export const trackSentimentPromptDismissed = () => {
    analytics.track("sentiment_prompt_dismissed");
};
/* -------------------------------------------------------------------------- */
/* Credit-gate dialogs                                                        */
/* -------------------------------------------------------------------------- */
/*
  Only what the user does with a dialog. Every credit spend is fired server-side,
  since the backend is what debits.

  `subsection` uses the same vocabulary as `signup_subsection`, so gate events
  and signups line up without a mapping table. `context` says which dialog
  asked, since a club-import gate is a prerequisite rather than a paywall.
*/
const CREDITS_SECTION = "Credits";
export const creditDialogProps = ({ context, featureId, reason, }) => ({
    section: CREDITS_SECTION,
    subsection: featureId ?? "none",
    context,
    action_type: "lifecycle",
    ...(reason && { reason }),
});
/**
 * The three event names, in one place. Web fires them through its own transport
 * (events/creditMixpanel.ts) and mobile through the port below, so the names are
 * the only thing keeping the two funnels the same event.
 */
export const CREDIT_DIALOG_EVENT = "credit_dialog";
export const CREDIT_DIALOG_ACTION_EVENT = "credit_dialog_action";
export const LEARN_MORE_EVENT = "learn_more";
/** The dialog was shown. Denominator for everything below. */
export const trackCreditDialog = (params) => analytics.track(CREDIT_DIALOG_EVENT, creditDialogProps(params));
/**
 * How the dialog was left. Not the button label: "Maybe later", the X and a
 * swipe-down are one answer, and per-dialog vocabulary can't be compared.
 */
export const trackCreditDialogAction = (params) => analytics.track(CREDIT_DIALOG_ACTION_EVENT, {
    ...creditDialogProps(params),
    value: params.value,
});
/** "Learn more", the only exit to pricing. Same event as the SBC side panel. */
export const trackLearnMore = (params) => analytics.track(LEARN_MORE_EVENT, creditDialogProps(params));
/* -------------------------------------------------------------------------- */
/* Club-import nudges                                                         */
/* -------------------------------------------------------------------------- */
/*
  Deliberately NOT part of the credit_dialog family above.

  A nudge is a pitch, not a refusal: it is raised proactively, most often at a
  logged-out visitor who has no balance and has never been shown what a credit
  is. Filing it under `section: "Credits"` would put that audience in every
  Credits report, and the nudge fires on ordinary solves and builds, so it
  would outnumber the real credit dialogs and move their rates.

  `subsection` also means something different here. In the credit family it is
  the feature that was refused; a nudge refuses nothing, so section/subsection
  carry the surface the pitch was raised on — the same pair `club_import_cta`
  already sends, which is what makes the two line up.
*/
export const CLUB_IMPORT_NUDGE_EVENT = "club_import_nudge";
export const CLUB_IMPORT_NUDGE_ACTION_EVENT = "club_import_nudge_action";
export const clubImportNudgeProps = ({ nudgeId, section, subsection, }) => ({
    section,
    subsection: subsection ?? "none",
    nudge_id: nudgeId,
    action_type: "lifecycle",
});
/** The nudge was shown. Denominator for the action below. */
export const trackClubImportNudge = (params) => analytics.track(CLUB_IMPORT_NUDGE_EVENT, clubImportNudgeProps(params));
/**
 * How the nudge was left. Not the button label: the dismiss button, the X, the
 * backdrop and Escape are one answer, and per-nudge copy cannot be compared.
 */
export const trackClubImportNudgeAction = (params) => analytics.track(CLUB_IMPORT_NUDGE_ACTION_EVENT, {
    ...clubImportNudgeProps(params),
    value: params.value,
});
//# sourceMappingURL=events.js.map